import CollapseWithLinkButton from './components/CollapseWithLinkButton';
import { TranslationService } from '../../../../services/translation.service';
import Checkbox from './components/Checkbox';
import LocationAutocomplete from '../../../Shared/LocationAutocomplete';
import { LocationAutocompleteSelectionType } from '../../../Shared/LocationAutocomplete/location-autocomplete-selection-type.enum';
import { LocationType } from '../../../../models/location-type.enum';
import ValidationInfo from '../../../../validation/validation-info';
import RadioButton from './components/RadioButton';
import { InterchangeDuration } from './models/interchange-duration.enum';
import exclamationCircleYellowIcon from '../../../../../assets/icons/exclamation-circle-yellow.svg';
import DataUrlHelper from '../../../../helpers/data-url.helper';
import TravelByFootOrBike from './components/TravelByFootOrBike';
import { TransportMode } from '../../../../models/transport-mode.enum';
import TransportModeFilter from '../../../Shared/TransportModeFilter';
import { ConfigurationService } from '../../../../services/configuration.service';
import { TransportSubMode } from '../../../../models/transport-sub-mode.enum';
import ClearButton from '../../../Shared/ClearButton';
import { SanitizerService } from '../../../../services/sanitizer.service';
import { filtersAreEqual, initialTravelPlannerFilters, initialTravelPlannerFiltersWithBussOnDemand, type ITravelPlannerFilters } from './models/travel-planner-filters.model';
import { FilterHelper } from './helpers/filter.helper';
import { ClientSideStorageItems, ClientSideStorageService } from '../../../../services/client-side-storage.service';
import { ClientSideStorageType } from '../../../../models/client-side-storage-type.enum';
import { useEffect, useState } from 'react';

function createCollapseLinkText(numberOfFiltersSelected: number): string {
  if (numberOfFiltersSelected === 0) {
    return TranslationService.get('travelplanner.moreoptions');
  }
  if (numberOfFiltersSelected === 1) {
    return `${TranslationService.get('travelplanner.moreoptions')} (1 ${TranslationService.get('travelplanner.changedfilter').toLocaleLowerCase()})`;
  }
  return `${TranslationService.get('travelplanner.moreoptions')} (${numberOfFiltersSelected} ${TranslationService.get('travelplanner.changedfilters').toLocaleLowerCase()})`;
}

function TravelPlannerFilters({
  mostRecentlyUsed,
  state,
  onChange,
  onSaveSelectionsChange,
}: {
  mostRecentlyUsed: ITravelPlannerFilters;
  state: ITravelPlannerFilters;
  onChange: (state: ITravelPlannerFilters) => void;
  onSaveSelectionsChange: (value: boolean) => void;
}) {
  // === CONFIGURATION ===
  const bussOnDemandEnabled = ConfigurationService.get('travelplanner.enablebussondemandintravelplannerfeatureflag') === 'true';
  const [functionalConsentGiven, setFunctionalConsentGiven] = useState(ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional));
  const [travelPlannerFiltersSaveChange, setTravelPlannerFiltersSaveChange] = useState(false);

  // === VARIABLES ===
  // NV-5212 - Add buss on demand transport mode if feature flag is enabled
  const availableTransportModes = [TransportMode.tram, TransportMode.bus, TransportMode.ferry, ...(bussOnDemandEnabled ? [TransportMode.bussondemand] : [])];
  const isDirty = !filtersAreEqual(state, mostRecentlyUsed);
  const numberOfChangedFilters = FilterHelper.numberOfFiltersChanged(state, bussOnDemandEnabled ? initialTravelPlannerFiltersWithBussOnDemand : initialTravelPlannerFilters);
  const saveSelections = ClientSideStorageService.getLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFiltersSave) !== '0';

  useEffect(() => {
    if (!ClientSideStorageService.isConsentStatusSet()) {
      ClientSideStorageService.onConsentStatusChange()
        .then(() => {
          const hasConsentChanged = ClientSideStorageService.isConsentGiven(ClientSideStorageType.Functional);
          if (hasConsentChanged) {
            setFunctionalConsentGiven(hasConsentChanged);
          }
        })
        .catch(() => {});
    }
  }, []);

  return (
    <div>
      <CollapseWithLinkButton ariaLive="polite" linkText={createCollapseLinkText(numberOfChangedFilters)}>
        <h2 className="visually-hidden">{TranslationService.get('travelplanner.moreoptions')}</h2>
        <div className="travel-planner-filters">
          <div className="mb-3">
            <h3 className="h4">{TranslationService.get('travelplanner.route')}</h3>
            <Checkbox
              id="no-transfers"
              label={TranslationService.get('travelplanner.notransfers')}
              checked={state.onlyDirectConnections}
              onChange={(value) => {
                onChange({ ...state, onlyDirectConnections: value });
              }}
            />
          </div>
          <div className="mb-3 border-bottom pb-3">
            <LocationAutocomplete
              id="via"
              labelClassName="h4"
              locationTypes={[LocationType.stopArea]}
              labelText="Via"
              placeholderText={TranslationService.get('common.stop')}
              state={{ value: state.viaStopArea ? { type: LocationAutocompleteSelectionType.location, location: state.viaStopArea } : undefined, validationInfo: new ValidationInfo() }}
              onSelect={(value) => {
                onChange({ ...state, viaStopArea: value?.location });
              }}
            />
          </div>
          {!state.onlyDirectConnections && (
            <fieldset id="interchange-duration" className="mb-3 border-bottom pb-3">
              <legend className="h4">{TranslationService.get('travelplanner.interchangeduration')}</legend>
              <RadioButton
                id="interchange-duration-short"
                name="interchange-duration"
                labelText={TranslationService.get('travelplanner.interchangedurationshort')}
                value={InterchangeDuration.short}
                checked={state.interchangeDuration === InterchangeDuration.short}
                helpIconSrc={DataUrlHelper.encode(exclamationCircleYellowIcon)}
                helpText={TranslationService.get('travelplanner.interchangedurationwarning')}
                className="mb-2"
                onChange={(value) => {
                  onChange({ ...state, interchangeDuration: value as InterchangeDuration });
                }}
              />
              <RadioButton
                id="interchange-duration-normal"
                name="interchange-duration"
                labelText={TranslationService.get('travelplanner.interchangedurationnormal')}
                value={InterchangeDuration.normal}
                checked={state.interchangeDuration === InterchangeDuration.normal}
                onChange={(value) => {
                  onChange({ ...state, interchangeDuration: value as InterchangeDuration });
                }}
              />
            </fieldset>
          )}
          <div className="mb-3 border-bottom pb-3">
            <h3 className="h4">{TranslationService.get('travelplanner.nearbystopareas')}</h3>
            <Checkbox
              id="nearby-stopareas"
              label={TranslationService.get('travelplanner.transfernearbystopareas')}
              checked={state.includeNearbyStopAreas}
              onChange={(value) => {
                onChange({ ...state, includeNearbyStopAreas: value });
              }}
            />
          </div>
          <fieldset className="mb-3 border-bottom pb-3">
            <legend className="h4">{TranslationService.get('travelplanner.alternativejourneys')}</legend>
            <TravelByFootOrBike
              className="mt-1"
              state={{
                showWalking: state.showWalking,
                showBiking: state.showBiking,
                bikeMaxDistance: state.bikingMaxDistance,
                bikeSpeed: state.bikingSpeed,
              }}
              onChange={(value) => {
                onChange({
                  ...state,
                  showWalking: value.showWalking,
                  showBiking: value.showBiking,
                  bikingMaxDistance: value.bikeMaxDistance,
                  bikingSpeed: value.bikeSpeed,
                });
              }}
            />
          </fieldset>
          <fieldset className="mb-3 border-bottom pb-3">
            <legend className="h4">{TranslationService.get('travelplanner.transportmode')}</legend>
            <TransportModeFilter
              className="mt-1"
              availableTransportModes={availableTransportModes}
              availableTransportSubModes={[TransportSubMode.vasttagen, TransportSubMode.longDistanceTrain, TransportSubMode.regionalTrain]}
              groupNonVasttagenAsOtherTrains={true}
              selectedTransportModes={state.transportModes}
              selectedTransportSubModes={state.transportSubModes}
              onChange={(transportModes, transportSubModes) => {
                onChange({ ...state, transportModes, transportSubModes });
              }}
            />
          </fieldset>
          <fieldset className="mb-3 border-bottom pb-3">
            <legend className="h4">{TranslationService.get('travelplanner.settings')}</legend>
            <div className="d-flex flex-wrap flex-column">
              <Checkbox
                id="show-occupancy"
                className="mb-2"
                label={TranslationService.get('travelplanner.useoccupancy')}
                checked={state.showOccupancy}
                onChange={(value) => {
                  onChange({ ...state, showOccupancy: value });
                }}
              />
              <Checkbox
                id="save-selections"
                label={TranslationService.get('travelplanner.saveselections')}
                checked={!!saveSelections && !!functionalConsentGiven}
                disabled={!functionalConsentGiven}
                helpText={
                  !functionalConsentGiven ? (
                    <>

                      {TranslationService.get('travelplanner.saveselectionscookie')}
                      <a href={TranslationService.get('common.cookiepageurl')}>{TranslationService.get('common.managecookies')}</a>
                    </>
                  ) : undefined
                }
                interactiveHelpText={true}
                onChange={(value) => {
                  ClientSideStorageService.setLocalStorage(ClientSideStorageItems.LocalStorage.TravelPlannerFiltersSave, value ? '1' : '0');
                  setTravelPlannerFiltersSaveChange(!travelPlannerFiltersSaveChange);
                  onSaveSelectionsChange(value);
                }}
              />
            </div>
          </fieldset>
          <div className="mb-3">
            <ClearButton
              onClick={() => {
                // NV-5212
                onChange(bussOnDemandEnabled ? initialTravelPlannerFiltersWithBussOnDemand : initialTravelPlannerFilters);
              }}
            />
          </div>
        </div>
      </CollapseWithLinkButton>
      <div aria-live="polite">
        {isDirty && (
          <div
            className="filter-help-text bg-gray p-3 mb-3 text-center last-child-margin-bottom-none"
            dangerouslySetInnerHTML={{ __html: SanitizerService.sanitizeHtml(TranslationService.get('travelplanner.filterchangedhelptext')) }}
          />
        )}
      </div>
    </div>
  );
}

export default TravelPlannerFilters;
